import React, { useState } from 'react';

import axios from 'axios';
import axiosRetry from 'axios-retry';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

// Create a new axios instance with retry configuration
const axiosInstance = axios.create();
axiosRetry(axiosInstance, {
  retries: 3,
  retryDelay: (retryCount) => {
    return retryCount * 1000; // wait 1s, 2s, 3s between retries
  },
  retryCondition: (error) => {
    // Retry on network errors or 5xx server errors
    return axiosRetry.isNetworkOrIdempotentRequestError(error) || error.response.status >= 500;
  },
});

const ProgressBar = ({ stage }) => {
  const stages = ['Uploading', 'Organizing', 'Analyzing', 'Done'];
  const currentIndex = stages.indexOf(stage);

  return (
    <div className="progress-modal">
      <div className="progress-bar">
        {stages.map((s, index) => (
          <div
            key={s}
            className={`progress-stage ${index <= currentIndex ? 'active' : ''} ${s === 'Done' ? 'done' : ''}`}
          >
            {s}
            {s === 'Done' && index <= currentIndex && <span className="checkmark">✓</span>}
          </div>
        ))}
      </div>
    </div>
  );
};

const Review = ({ formData, questions, goToStep, inviteCode }) => {
  const [loading, setLoading] = useState(false);
  const [uploadStage, setUploadStage] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const navigate = useNavigate();

  const simulateStage = (stage, duration) => {
    return new Promise((resolve) => {
      setUploadStage(stage);
      setTimeout(resolve, duration);
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    setValidationResult(null);
    setUploadStage('Uploading');

    // Validate that all questions have answers
    const hasAllAnswers = formData.answers.every(answer => answer.text.trim() !== '');
    if (!hasAllAnswers) {
      setValidationResult('Please answer all questions before submitting.');
      setLoading(false);
      setUploadStage(null);
      return;
    }

    const submissionData = new FormData();
    const uuid = uuidv4();
    
    // Add metadata
    submissionData.append('uuid', uuid);
    submissionData.append('inviteCode', inviteCode);
    submissionData.append('companyName', formData.companyName);
    submissionData.append('contactName', formData.contactName);
    submissionData.append('contactEmail', formData.contactEmail);
    
    // Add answers as JSON strictly following the API Guide format
    const answersJson = formData.answers.map((answer, index) => {
      // API expects only questionId and the raw answer text in this JSON payload.
      // Files are handled separately by the `attachment_qX_Y` fields.
      return {
        questionId: index + 1, // Corrected: Start questionId from 1
        answer: answer.text // Use only the raw answer text
      };
    });
    submissionData.append('answers', JSON.stringify(answersJson));
    
    // Add attachments with the naming convention expected by the API
    formData.answers.forEach((answer, questionIndex) => {
      answer.attachments.forEach((file, fileIndex) => {
        // Corrected: Use questionIndex + 1 to match the questionId convention (starting from 1)
        submissionData.append(
          `attachment_q${questionIndex + 1}_${fileIndex}`, 
          file, 
          file.name
        );
      });
    });

    try {
      await simulateStage('Uploading', 2000);
      await simulateStage('Organizing', 1500);
      await simulateStage('Analyzing', 2500);

      const apiBase = process.env.REACT_APP_API_BASE_URL || '';
      const apiUrl = `${apiBase}/api/submit`;
      
      const response = await axiosInstance.post(apiUrl, submissionData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.status === 200) {
        await simulateStage('Done', 1000);
        navigate('/thank-you');
      } else {
        console.error('Unexpected response from server:', response);
        setValidationResult(`Submission failed: ${response.data.message}`);
      }
    } catch (error) {
      if (error.response) {
        console.error('Error response from server:', error.response);
        // Parse the API error response according to the expected format
        const errorData = error.response.data;
        if (errorData && errorData.success === false) {
          // Use the API's error and message fields if available
          const errorCode = errorData.error || 'Unknown error';
          const errorMessage = errorData.message || 'No detailed message provided';
          setValidationResult(`Submission failed: ${errorCode} - ${errorMessage}`);
        } else {
          // Fallback for unexpected error format
          setValidationResult(`Submission failed: ${error.response.status} ${error.response.statusText}`);
        }
      } else if (error.request) {
        console.error('No response received from server:', error.request);
        setValidationResult('Submission failed: No response from server. Please check your internet connection.');
      } else {
        console.error('Error during submission:', error.message);
        setValidationResult(`Submission failed: ${error.message}`);
      }
    } finally {
      setLoading(false);
      setUploadStage(null);
    }
  };

  return (
    <div className="review-container">
      <h2>Review Your Answers</h2>
      <p>Please review your answers before submitting. You can go back to edit any question if needed.</p>
      
      <div className="company-info-review">
        <h3>Company Information</h3>
        <p><strong>Company Name:</strong> {formData.companyName}</p>
        <p><strong>Contact Name:</strong> {formData.contactName}</p>
        <p><strong>Contact Email:</strong> {formData.contactEmail}</p>
      </div>
      
      <div className="answers-review">
        {questions.map((question, index) => (
          <div key={index} className="answer-review-item">
            <h3>Question {index + 1}:</h3>
            <p className="question-text">{question}</p>
            <div className="answer-text">
              <p>{formData.answers[index].text}</p>
            </div>
            
            {formData.answers[index].attachments.length > 0 && (
              <div className="attachments-review">
                <h4>Attachments:</h4>
                <ul>
                  {formData.answers[index].attachments.map((file, fileIndex) => (
                    <li key={fileIndex}>{file.name}</li>
                  ))}
                </ul>
              </div>
            )}
            
            <button 
              type="button" 
              onClick={() => goToStep(index + 1)}
              className="edit-button"
            >
              Edit
            </button>
          </div>
        ))}
      </div>
      
      <div className="submission-controls">
        <button 
          type="button" 
          onClick={() => goToStep(formData.answers.length)}
          className="prev-button"
        >
          Back
        </button>
        <button 
          type="button" 
          onClick={handleSubmit}
          disabled={loading}
          className="submit-button"
        >
          {loading ? 'Submitting...' : 'Submit Assessment'}
        </button>
      </div>
      
      {uploadStage && <ProgressBar stage={uploadStage} />}
      {validationResult && <p className="error-message">{validationResult}</p>}
    </div>
  );
};

export default Review;
